import { useEffect, useCallback } from 'react';
import { invokeAsExtension } from '../../extensions/capabilities/enforce';
import { setDbManagerTabs, setDbScriptSaveRequest } from '../../store/tabsStore';
import { dbScriptMetaForTab, currentScriptScope } from '../../extensions/dbEngines';
import { cellToText } from '../dbCellText';
import { parseCellKey, cellKey } from '../dbGridModel';
import { shouldGridHandleKey } from './dbGridKeyGuard';
import { fieldOwnsClipboard, planPaste, toClipboardField } from './dbGridClipboard';
import { isSelectionSafeTarget } from './dbGridClickOutside';

/** Keyboard and clipboard handling for the results grid and SQL editor. */
export function useDbGridShortcuts({
  tab,
  result,
  isQueryTab,
  sqlDraft,
  setError,
  pendingRows,
  pendingEdits,
  editingCell,
  selectedRows,
  setSelectedRows,
  selectedCells,
  setSelectedCells,
  selectionAnchorRef,
  setCellAnchor,
  transposed,
  setTransposed,
  findOpen,
  setFindOpen,
  setFindFocusNonce,
  setFindIndex,
  setHistoryOpen,
  promotedRowsRef,
  saveChanges,
  hasUnsavedGridChanges,
  rememberScrollAcrossFlip,
  drawnColIndexes,
  formatDraft,
  pendingEditCount,
  pendingDeleteCount,
  discardChanges,
  toggleDeleteMark,
  duplicateSelectedRows,
  visualRows,
  stepFind,
  findPosRef,
  closeFind,
  applyCellWrites,
}) {
  // Ctrl+C / Ctrl+V over a cell selection. These ride the native copy/paste
  // events rather than a keydown handler: the events fire for the same
  // keystrokes but hand us the clipboard synchronously, with no permission
  // prompt and none of navigator.clipboard.readText()'s focus requirements.
  useEffect(() => {
    if (selectedCells.size === 0) return undefined;

    // Position each selected cell in the grid's visual top-to-bottom order so
    // copy can lay them out as a rectangle and paste can anchor a block.
    const rowPos = new Map(visualRows.map((r, i) => [`${r.kind}:${r.id}`, i]));
    const cells = [...selectedCells]
      .map(parseCellKey)
      .map((c) => ({ ...c, row: rowPos.get(`${c.kind}:${c.id}`) }))
      .filter((c) => c.row !== undefined);
    if (cells.length === 0) return undefined;

    const rowLo = Math.min(...cells.map((c) => c.row));
    const colLo = Math.min(...cells.map((c) => c.col));

    // The value the grid is currently showing: a staged edit wins over the
    // row's stored value, so copying a cell you just edited copies the edit.
    const effectiveValue = (kind, id, col) => {
      if (kind === 'p') return pendingRows.find((r) => r.id === id)?.values[col] ?? '';
      const key = `${id}:${col}`;
      if (Object.prototype.hasOwnProperty.call(pendingEdits, key)) return pendingEdits[key];
      return result?.rows?.[id]?.[col];
    };

    // An active inline editor owns the clipboard, and so does every text
    // field, selection or not: a paste into an empty WHERE box or the Edit
    // cell editor is the field's (dbManager/dbGridClipboard.js).
    const shouldHandle = (e) => {
      if (editingCell) return false;
      // Not from a modal (Edit cell) or from any editor: Monaco keeps its own
      // selection — its hidden textarea has none — so the check below let the
      // grid overwrite what Monaco had just put on the clipboard with the WHOLE
      // cell, for Ctrl+C and right-click → Copy alike (owner, 06.10.2026).
      if (!shouldGridHandleKey(e)) return false;
      return !fieldOwnsClipboard(e.target);
    };

    const handleCopy = (e) => {
      if (!shouldHandle(e)) return;
      e.preventDefault();
      const rowHi = Math.max(...cells.map((c) => c.row));
      const colHi = Math.max(...cells.map((c) => c.col));
      const byPos = new Map(cells.map((c) => [`${c.row}:${c.col}`, c]));
      const lines = [];
      // Visible columns only, so the block that lands on the clipboard has the
      // shape the user sees — and so a copy/paste round-trip lines up.
      const copyCols = drawnColIndexes.filter((c) => c >= colLo && c <= colHi);
      // One cell copies raw (JSON stays JSON in a text editor); a block quotes
      // the values that hold a tab or line break, so it pastes back in place.
      const asField = rowHi > rowLo || copyCols.length > 1 ? toClipboardField : (v) => v;
      for (let r = rowLo; r <= rowHi; r++) {
        const out = [];
        for (const c of copyCols) {
          const hit = byPos.get(`${r}:${c}`);
          // Holes in a non-rectangular selection copy as empty, so the
          // pasted block keeps the shape the user selected.
          out.push(hit ? asField(cellToText(effectiveValue(hit.kind, hit.id, hit.col))) : '');
        }
        lines.push(out.join('\t'));
      }
      e.clipboardData.setData('text/plain', lines.join('\n'));
    };

    const handlePaste = (e) => {
      if (isQueryTab || !shouldHandle(e)) return;
      const text = e.clipboardData.getData('text/plain');
      e.preventDefault();
      const writes = [];
      const plan = planPaste(text, { selectedRows: new Set(cells.map((c) => c.row)).size });
      if (plan.mode === 'block') {
        // Multi-cell clipboard: drop it in as a rectangle anchored at the
        // top-left selected cell, clipped to the rows/columns that exist —
        // pasting past the last row does not create rows.
        // Land the block across the columns the user can SEE, starting at the
        // anchor — the mirror of what handleCopy emits, so a copy/paste
        // round-trip puts each value back where it came from. Walking raw
        // indexes instead let a value drop onto a hidden column, where the
        // staged edit is invisible until Save writes it to the server.
        const pasteCols = drawnColIndexes.filter((c) => c >= colLo);
        plan.block.forEach((line, dr) => {
          const target = visualRows[rowLo + dr];
          if (!target) return;
          line.forEach((value, dc) => {
            const col = pasteCols[dc];
            // Past the last visible column: pasting does not create columns.
            if (col === undefined) return;
            writes.push({ kind: target.kind, id: target.id, col, value });
          });
        });
      } else {
        // One value — a multi-line JSON included: fill every selected cell.
        for (const c of cells) writes.push({ kind: c.kind, id: c.id, col: c.col, value: plan.value });
      }
      applyCellWrites(writes);
    };

    /**
     * Ctrl+X — copy the selection, then stage NULL over it.
     *
     * On the native `cut` event for the same reason copy and paste are: the
     * event fires for the keystroke AND hands us the clipboard synchronously,
     * with no permission prompt. A keydown binding would have to go back
     * through `navigator.clipboard`, which needs focus it does not reliably
     * have here.
     *
     * `handleCopy` is reused verbatim rather than reimplemented so cut and
     * copy can never disagree about what the selection contains — same
     * rectangle, same staged-edit-wins value, same hidden-column filtering.
     *
     * The clear is STAGED, like every other edit: it shows as a pending change
     * and Save or Escape decides its fate. Read-only surfaces are refused —
     * `writable` covers query tabs and connections without a writable table.
     */
    const handleCut = (e) => {
      if (!shouldHandle(e)) return;
      handleCopy(e);
      if (isQueryTab) return;
      // Pending rows keep their own values; blanking one is what its own
      // inputs are for, and `applyCellWrites` addresses them by temp id.
      const writes = cells
        .filter((c) => c.kind === 'r')
        .map((c) => ({ kind: c.kind, id: c.id, col: c.col, value: null }));
      if (writes.length > 0) applyCellWrites(writes);
    };

    window.addEventListener('copy', handleCopy);
    window.addEventListener('cut', handleCut);
    window.addEventListener('paste', handlePaste);
    return () => {
      window.removeEventListener('copy', handleCopy);
      window.removeEventListener('cut', handleCut);
      window.removeEventListener('paste', handlePaste);
    };
  }, [selectedCells, visualRows, pendingEdits, pendingRows, result, editingCell, isQueryTab, applyCellWrites, drawnColIndexes]);

  // Ctrl+S on a query tab: named scripts save straight to disk
  // (scripts/db/<name>.sql); unnamed ones open the save-as modal, which asks
  // for the name and patches the tab (DbScriptSaveModal).
  const saveScript = useCallback(() => {
    if (!tab || tab.tableName) return;
    if (tab.scriptName) {
      // The sidecar (<name>.json) records which database the script belongs
      // to — unless the user saved it unlinked (scriptLinked false), in
      // which case passing null meta clears any stale sidecar too.
      const meta = tab.scriptLinked === false ? null : dbScriptMetaForTab(tab);
      invokeAsExtension('db_script_write', { extensionId: tab.extensionId, name: tab.scriptName, content: sqlDraft, meta, scope: currentScriptScope() })
        .then(() => {
          setDbManagerTabs((prev) =>
            prev.map((t) => (t.id === tab.id ? { ...t, savedSql: sqlDraft, hasUnsavedChanges: false } : t))
          );
        })
        .catch((e) => setError(String(e)));
      return;
    }
    setDbScriptSaveRequest({ tabId: tab.id });
  }, [tab, sqlDraft, setError]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!shouldGridHandleKey(e)) return; // a modal on top owns the keyboard
      // Ctrl+F opens the find bar, or re-focuses it when already open (the bar
      // selects its text on mount, so a second Ctrl+F replaces the search).
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'f') {
        e.preventDefault();
        if (findOpen) {
          setFindFocusNonce((v) => v + 1);
        } else {
          setFindOpen(true);
          setFindIndex(0);
          // Opening the bar KEEPS the selection (owner, 30.09.2026: like
          // DataGrip / Excel / VS Code). The active match has its own amber
          // highlight; the search starts from the selected cell.
          findPosRef.current = null;
        }
        return;
      }
      // F3 / Shift+F3 walk matches without the bar having focus, so you can
      // click a cell, keep the keyboard on the grid, and still step through.
      if (e.key === 'F3' && findOpen) {
        e.preventDefault();
        stepFind(e.shiftKey ? -1 : 1);
        return;
      }
      if (e.key === 'Escape' && findOpen && !editingCell) {
        e.preventDefault();
        closeFind();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        if (isQueryTab && tab) {
          e.preventDefault();
          saveScript();
          return;
        }
        // Staged deletes count too — Save is their confirmation, so Ctrl+S has
        // to fire when a delete is the ONLY thing staged.
        if (pendingRows.length > 0 || pendingEditCount > 0 || pendingDeleteCount > 0) {
          e.preventDefault();
          saveChanges();
        }
        return;
      }
      /**
       * Ctrl+A — select everything in the result, the way DataGrip / Excel do
       * (owner, 06.10.2026: with a row or a cell selected — e.g. after a WHERE —
       * it selected the whole page's text, tabs and sidebar instead).
       *  - rows selected (row numbers) → every row of the result;
       *  - a cell / column selected → every cell, so Ctrl+C copies the lot;
       *  - inside a text field (WHERE, find bar) or an editor (SQL, Monaco) it
       *    stays that field's own select-all.
       */
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'a' && !editingCell) {
        const t = e.target;
        const tag = (t?.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || t?.isContentEditable) return;
        if (t instanceof Element && t.closest('.monaco-editor')) return;
        const inGrid = t instanceof Element && !!t.closest('.dbm-grid-wrap');
        const hasSelection = selectedRows.size > 0 || selectedCells.size > 0;
        // Focus on nothing in particular (the page) is the grid's too: this tab
        // only mounts while it is on screen, and the page select-all painted
        // the whole app blue (owner, 07.10.2026; DataGrip selects the grid).
        // A focused control elsewhere — the sidebar tree — keeps its own.
        const onPage = t === document.body || t === document.documentElement;
        if (!inGrid && !hasSelection && !onPage) return;
        if (!result?.rows?.length) return;
        e.preventDefault();
        if (transposed) return; // the record view of every row would be thousands of columns
        if (selectedRows.size > 0 && selectedCells.size === 0) {
          setSelectedRows(new Set(result.rows.map((_, i) => i)));
          selectionAnchorRef.current = 0;
          return;
        }
        const all = new Set();
        for (const r of visualRows) for (const c of drawnColIndexes) all.add(cellKey(r.kind, r.id, c));
        setSelectedCells(all);
        const first = visualRows[0];
        if (first && drawnColIndexes.length) setCellAnchor({ kind: first.kind, id: first.id, col: drawnColIndexes[0] });
        return;
      }
      // A single cell mid-edit handles its own Escape (cancelEditCell, via
      // the input's onKeyDown) without discarding already-committed edits on
      // other cells. Only treat Escape as "discard everything"/"deselect"
      // once there's no active cell editor left to intercept it.
      // A cell selection deselects first and stops there, rather than falling
      // through to "discard everything" below — a paste leaves its target
      // cells selected, so the reflex Escape after one must not undo it.
      // Applies on query tabs too, where cells are selectable but read-only.
      /**
       * Escape, in priority order — and STAGED CHANGES WIN.
       *
       * Escape is the Cancel button's keyboard twin, so as long as anything is
       * staged it discards, whatever else is selected. That matters most for
       * the delete mark: marking a row and hitting Escape has to un-mark it,
       * not quietly clear the selection and leave the row still doomed.
       *
       * The cell branch used to come first and return, which made Escape mean
       * two different things depending on how you had selected. Its stated
       * reason — a paste leaves its target cells selected and the reflex
       * Escape must not lose that selection — is preserved by NOT clearing any
       * selection while discarding: the paste is undone, the cells stay
       * selected, and a second Escape clears them.
       *
       * Then the cell selection, then the row selection. Query tabs skip the
       * discard (nothing to stage) but still deselect.
       */
      if (e.key === 'Escape' && !editingCell) {
        const hasPending =
          !isQueryTab &&
          (pendingRows.length > 0 || pendingEditCount > 0 || pendingDeleteCount > 0);
        if (hasPending) {
          e.preventDefault();
          discardChanges();
          return;
        }
        if (selectedCells.size > 0) {
          e.preventDefault();
          setSelectedCells(new Set());
          setCellAnchor(null);
          return;
        }
        if (selectedRows.size > 0) {
          e.preventDefault();
          setSelectedRows(new Set());
          selectionAnchorRef.current = null;
        }
      }
      // DBeaver-style transposed/record view — Tab flips selected rows from
      // columns-across to columns-down. Guarded to a non-editable target so
      // it doesn't hijack ordinary Tab focus-navigation in the filter bar,
      // SQL editor, or any other input on the page.
      // Query tabs included. The `!isQueryTab` that used to be here was the
      // odd one out: every other isQueryTab guard in this file blocks a WRITE
      // — editing a cell, pasting, marking a row deleted — because a result
      // set from arbitrary SQL has no table to write back to. Flipping a row
      // to read it vertically is a read, and it is *more* useful on a query
      // tab, where a wide join is exactly what you cannot read across.
      // The isEditableTarget check below is what actually keeps Tab working
      // as focus-navigation in the SQL editor and filter boxes.
      if (e.key === 'Tab' && !editingCell) {
        const tag = (e.target?.tagName || '').toLowerCase();
        const isEditableTarget = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable;
        if (!isEditableTarget) {
          if (selectedRows.size > 0) {
            e.preventDefault();
            // The flip unmounts the horizontal grid; without this the trip
            // back lands at the top (see rememberScrollAcrossFlip).
            rememberScrollAcrossFlip();
            setTransposed((wasTransposed) => {
              // Coming BACK from a record view we entered by promoting a cell
              // selection: drop the row selection again. It was only ever
              // added to give that view a column to draw — you selected a
              // cell, not a row, and leaving the whole row lit on return is
              // the "it focuses the whole row" complaint. The cell selection
              // is untouched, so what stays highlighted is what you picked.
              if (wasTransposed && promotedRowsRef.current) {
                promotedRowsRef.current = false;
                setSelectedRows(new Set());
                selectionAnchorRef.current = null;
              }
              return !wasTransposed;
            });
          } else if (selectedCells.size > 0) {
            // Only cells are selected — promote the (saved) rows they sit in
            // to a row selection and flip straight into the record view, so a
            // "select a cell, press Tab" reads that row top-to-bottom. Pending
            // rows have no place in the transposed view (its columns are saved
            // rows), so they're ignored here.
            const rows = new Set();
            for (const key of selectedCells) {
              const { kind, id } = parseCellKey(key);
              if (kind === 'r') rows.add(id);
            }
            if (rows.size > 0) {
              e.preventDefault();
              rememberScrollAcrossFlip();
              // Remembered so the trip back can undo exactly this — see the
              // branch above.
              promotedRowsRef.current = true;
              setSelectedRows(rows);
              selectionAnchorRef.current = Math.min(...rows);
              // The cell selection SURVIVES the promotion. It used to be
              // cleared here, which made a selected cell lose its highlight
              // the moment Tab flipped the view — while the same Tab pressed
              // mid-EDIT kept it, because that path was written later and
              // preserved it deliberately. Two ways to reach the record view,
              // two different answers to "where was I", for no reason a user
              // could see.
              //
              // Row and cell selection coexisting is fine and is what the
              // record view wants: `selectedRows` decides which columns it
              // draws, `selectedCells` decides which cell inside them is
              // highlighted. Both grids key the highlight off the same
              // `cellKey('r', row, col)`, so the same cell lights up either
              // side of the flip with nothing to translate.
              setTransposed(true);
            }
          }
        }
      }
      /**
       * Delete / Backspace marks the rows under the selection for deletion.
       * (Backspace over a CELL selection never gets here: it clears those
       * cells instead, as in Excel — dbManager/useDbGridKeyboardNav.js.)
       *
       * Guarded exactly like Ctrl+D below, and for sharper reasons: Delete and
       * Backspace are the two most overloaded keys on the keyboard. It is
       * refused on query tabs (nothing to delete from), while an editor is
       * open (the key belongs to the text), and on ANY editable target — which
       * is what keeps Backspace working normally in the WHERE box, the find
       * bar, the SQL editor and every modal input on the page. A grid with no
       * selection does nothing at all, so the key stays inert until you have
       * actually pointed at something.
       */
      if (
        (e.key === 'Delete' || e.key === 'Backspace') &&
        !isQueryTab &&
        !editingCell &&
        (selectedRows.size > 0 || selectedCells.size > 0)
      ) {
        const tag = (e.target?.tagName || '').toLowerCase();
        const isEditableTarget = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable;
        if (!isEditableTarget) {
          e.preventDefault();
          toggleDeleteMark();
        }
      }
      // Ctrl/Cmd+D duplicates the selected row(s) as pending inserts (PK
      // columns blanked, same as the context menu's "Duplicate row").
      // Editable-target guard keeps it from hijacking the shortcut inside
      // the filter bar, SQL editor, or any other text field.
      //
      // Fires from a CELL selection too, the same as Delete: both name a
      // record, so both should accept the same way of pointing at one. This
      // used to require `selectedRows` and did nothing at all from a cell,
      // which read as the shortcut being broken rather than as the selection
      // being the wrong kind.
      if (
        (e.metaKey || e.ctrlKey) &&
        e.key.toLowerCase() === 'd' &&
        !isQueryTab &&
        !editingCell &&
        (selectedRows.size > 0 || selectedCells.size > 0)
      ) {
        const tag = (e.target?.tagName || '').toLowerCase();
        const isEditableTarget = tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable;
        if (!isEditableTarget) {
          e.preventDefault();
          duplicateSelectedRows();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // pendingDeleteCount is load-bearing, not decorative: Escape decides
    // between "discard" and "deselect" by reading it, and staging a delete
    // changes nothing else in this list — so without it a grid with ONLY
    // deletes staged saw a stale zero and deselected instead of discarding.
    // `transposed` is here for the same reason — Ctrl+F reads it directly to
    // decide whether clearing the selection would collapse the view.
  }, [pendingRows, pendingEditCount, pendingDeleteCount, transposed, saveChanges, isQueryTab, tab, saveScript, editingCell, discardChanges, selectedRows, selectedCells, duplicateSelectedRows, setCellAnchor, findOpen, stepFind, closeFind, rememberScrollAcrossFlip, toggleDeleteMark, result, visualRows, drawnColIndexes, setFindFocusNonce, setFindOpen, setFindIndex, findPosRef, setSelectedCells, setSelectedRows, selectionAnchorRef, setTransposed, promotedRowsRef]);

  /**
   * Query-tab shortcuts that Monaco would otherwise eat.
   *
   * The editor binds its own handlers on its DOM node and stops them there, so
   * a window listener on the bubble phase never sees Shift+Alt+F — Monaco takes
   * it for "Format Document", which does nothing useful because no SQL
   * formatter is registered with it. Capture runs before the target's own
   * handlers, so these win.
   *
   * History is Ctrl+Shift+H rather than Ctrl+H on purpose: Ctrl+H is Monaco's
   * find-and-replace, which is worth having in a SQL editor. Taking it would
   * trade a useful editor feature for a panel that has a button anyway.
   */
  useEffect(() => {
    if (!isQueryTab) return undefined;
    const onKey = (e) => {
      if (!shouldGridHandleKey(e)) return; // not the SQL behind a modal
      // e.code, not e.key. With Alt held the browser reports the character the
      // layout produces rather than the letter on the key, so `e.key === 'f'`
      // is false on most non-US layouts and on Windows generally — which is
      // why the cell editor's Alt+N above already matches on e.code. e.key is
      // still accepted as a fallback for events that carry no code.
      const isF = e.code === 'KeyF' || (e.key || '').toLowerCase() === 'f';
      if (e.altKey && e.shiftKey && !e.ctrlKey && !e.metaKey && isF) {
        e.preventDefault();
        e.stopPropagation();
        formatDraft();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'h') {
        e.preventDefault();
        e.stopPropagation();
        setHistoryOpen(true);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [isQueryTab, formatDraft, setHistoryOpen]);

  // Selection can empty out from several places (Escape, click-outside, row
  // delete, switching tabs) — rather than threading a revert into each of
  // those, just fall back to the normal grid whenever nothing is selected.
  useEffect(() => {
    if (selectedRows.size === 0) setTransposed(false);
  }, [selectedRows, setTransposed]);

  // Click outside the grid clears the row selection — otherwise a selected
  // row stays highlighted with no way back to "nothing selected" beyond
  // manually toggling every row off. The footer's left group is excluded: it
  // is the selection's action bar (Delete / Cancel / Save / Copy), every
  // button in it reads the selection at click time, and clearing on their own
  // mousedown empties it before the click lands.
  //
  // Worse than losing the selection, it cost a whole click. Delete is rendered
  // conditionally on the selection, so its mousedown unmounted it, the rest of
  // the group slid left, and mouseup landed on a different element than
  // mousedown did — which is not a click at all. Cancel needed pressing twice.
  useEffect(() => {
    if (selectedRows.size === 0 && selectedCells.size === 0) return undefined;
    const handleMouseDown = (e) => {
      // Also exclude the shared Modal (ConfirmModal etc.) — it renders as a
      // DOM sibling of the grid, not inside .dbm-grid-wrap, so a mousedown on
      // its own Confirm/Cancel buttons would otherwise clear the selection
      // (and collapse the transposed view behind it) while it's still open.
      // The find bar too (dbManager/dbGridClickOutside.js): clicking Aa dropped the record view.
      if (isSelectionSafeTarget(e.target)) return;
      setSelectedRows(new Set());
      selectionAnchorRef.current = null;
      setSelectedCells(new Set());
      setCellAnchor(null);
    };
    window.addEventListener('mousedown', handleMouseDown);
    return () => window.removeEventListener('mousedown', handleMouseDown);
  }, [selectedRows, selectedCells, setCellAnchor, setSelectedRows, selectionAnchorRef, setSelectedCells]);

  // Mirrors request tabs: unsaved edits surface as a dot on the tab itself
  // (EnhancedTabManager reads `hasUnsavedChanges` generically off any tab
  // type). Table tabs: pending grid edits. Query tabs: the SQL draft
  // diverging from the last saved script content (savedSql baseline).
  useEffect(() => {
    if (!tab) return;
    const desired = isQueryTab
      ? sqlDraft !== (tab.savedSql ?? '')
      : hasUnsavedGridChanges; // staged deletes too (card 50122def)
    setDbManagerTabs((prev) => {
      const current = prev.find((t) => t.id === tab.id);
      if (!current || !!current.hasUnsavedChanges === desired) return prev;
      return prev.map((t) => (t.id === tab.id ? { ...t, hasUnsavedChanges: desired } : t));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnsavedGridChanges, tab?.id, isQueryTab, sqlDraft, tab?.savedSql]);
}
